import { Autocomplete, TextField, Stack, SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Controller, Control, FieldError, FieldValues, Path } from 'react-hook-form';
import ErrorFormInput from '../error';
import { ReactNode } from 'react';

interface AutocompleteFormInputProps<T extends FieldValues> {
  parentSx?: SxProps;
  sx?: SxProps;
  required?: boolean;
  control: Control<T>;
  name: Path<T>;
  label?: string;
  placeholder?: string;
  error?: FieldError;
  options: AutocompleteFormInputOptionsProps[];
  getOptionLabel?: (option: AutocompleteFormInputOptionsProps) => string;
  renderOptionContent?: (option: AutocompleteFormInputOptionsProps) => ReactNode;
}

export interface AutocompleteFormInputOptionsProps {
  name: string;
  value: string;
  status?: string;
  sourcePartId?: string;
  purchaseType?: string;
  description?: string;
  quantityOrdered?: number;
  quantityReceived?: number;
}

export default function AutocompleteFormInput<T extends FieldValues = FieldValues>({
  parentSx,
  sx,
  required = false,
  control,
  name,
  label,
  placeholder,
  error,
  options,
  getOptionLabel,
  renderOptionContent
}: AutocompleteFormInputProps<T>) {
  const theme = useTheme();

  return (
    <Stack sx={parentSx}>
      <Controller
        rules={{ required: required }}
        control={control}
        name={name}
        defaultValue={'' as any}
        render={({ field: { onChange, value } }) => (
          <Autocomplete
            size="small"
            value={(() => {
              const matchedOption = options.find((option) => option.value === value);
              if (matchedOption) return matchedOption;
              return null;
            })()}
            onChange={(_, newValue) => {
              onChange(newValue ? newValue.value : '');
            }}
            options={options}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              return getOptionLabel ? getOptionLabel(option) : option.name;
            }}
            isOptionEqualToValue={(option, selectedValue) => {
              if (typeof selectedValue === 'string') {
                return option.value === selectedValue;
              }
              return option.value === selectedValue.value;
            }}
            renderOption={(props, option) => (
              <li {...props}>{renderOptionContent ? renderOptionContent(option) : option.name}</li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={placeholder}
                error={!!error}
                size="small"
                inputProps={{
                  ...params.inputProps,
                  sx: { padding: '0.6em 1em' }
                }}
                sx={{
                  backgroundColor: theme.palette.background.default,
                  borderRadius: '4px',
                  minWidth: '12em',
                  ...sx
                }}
              />
            )}
          />
        )}
      />
      {error && <ErrorFormInput error={error} />}
    </Stack>
  );
}
